// React core
import { useCallback, useEffect, useRef, useState } from "react";

// Third-party libraries
import {
  Calendar,
  Camera,
  Check,
  Headphones,
  Lock,
  Mic,
  Monitor,
  PhoneCall,
  Video,
} from "react-feather";

// App imports
import { SessionMode } from "../libs/constants/index.js";
import { AudioFormats } from "../libs/constants/recorder-config.js";
import useAppStore from "../store/AppStore.js";
import useLiveAvatarStore from "../store/LiveAvatarStore.js";
import { useBoundStore } from "../store/UseBoundStore1.js";
import MicCheck from "../components/DeviceSetup/MicCheck.js";

// Assets and styles
import logo from "../assests/images/logo.png";
import "../styles/UserPermissionsScreenNew.css";

// How long to wait for isSetupDone before giving up and surfacing an error
// instead of spinning forever (see onStartInterview / the isConnecting effect).
const SETUP_TIMEOUT_MS = 40000;

const UserPermissionsScreenNew = () => {
  // ============================================================================
  // REFS AND STATE
  // ============================================================================
  const videoRef = useRef(null);
  
  // Store state
  const {
    mode,
    isDesktop,
    setIsDesktop,
    firstName,
    jobTitle,
    organizationName,
  } = useBoundStore();

  const { permissions, setPermission } = useAppStore((state) => ({
    permissions: state.permissions,
    setPermission: state.setPermission,
  }));
  const { setScriptProcessor, isSetupDone, setShouldStartTheInterview } =
    useAppStore();
  const sessionError = useLiveAvatarStore((state) => state.sessionError);

  // Component state
  const [errorMessage, setErrorMessage] = useState({
    mic: "",
    camera: "",
    screen: "",
    message: "",
  });
  const [showMicTest, setShowMicTest] = useState(false);
  const [micStream, setMicStream] = useState(null);
  const [micTestPassed, setMicTestPassed] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [setupTimedOut, setSetupTimedOut] = useState(false);
  const [videoStream, setVideoStream] = useState(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const needMic = true;
  const needCam =
    mode === SessionMode.WEB_VIDEO ||
    mode === SessionMode.WEB_VIDEO_WITHOUT_AVATAR;
  const needScreen =
    isDesktop &&
    (mode === SessionMode.WEB_VIDEO ||
      mode === SessionMode.WEB_VIDEO_WITHOUT_AVATAR ||
      mode === SessionMode.WEB_AUDIO);
  // Telephonic audio travels over the real phone call, not the browser mic,
  // so testing the browser mic there would prove nothing and just add
  // friction before "Click to Receive Call".
  const needMicCheck = mode !== SessionMode.TELEPHONIC;

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const areAllRequiredPermissionsGranted = () => {
    const requiredPermissions = [];

    // Microphone is always required
    requiredPermissions.push("microphone");

    if (needCam) {
      requiredPermissions.push("webcam");
    }
    if (needScreen) {
      requiredPermissions.push(
        "tabDisplayRecording",
        "userAudioForDisplayStream"
      );
    }

    return requiredPermissions.every((perm) => permissions[perm]?.allowed);
  };

  // ============================================================================
  // AUDIO PROCESSING
  // ============================================================================

  const setupScriptProcessor = (stream) => {
    let audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 16000,
    });
    let gainNode = audioContext.createGain();
    gainNode.gain.value = 1.0;
    let scriptprocessor = audioContext.createScriptProcessor(512, 1, 1);
    let audioSource = audioContext.createMediaStreamSource(stream.clone());
    audioSource.connect(gainNode);
    gainNode.connect(scriptprocessor);
    scriptprocessor.connect(audioContext.destination);
    setScriptProcessor(scriptprocessor);
  };

  // ============================================================================
  // PERMISSION HANDLERS
  // ============================================================================

  const handleMicPermission = async () => {
    try {
      // Check if microphone permission is granted
      const permissionStatus = await navigator.permissions.query({
        name: "microphone",
      });

      const getPreferredMicStream = async () => {
        // Get the list of audio input devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );
        // Check if a Bluetooth device is connected
        const bluetoothDevice = audioInputDevices.find((device) =>
          device.label.toLowerCase().includes("bluetooth")
        );
        // Select the Bluetooth device if available, otherwise default to the first audio input
        const preferredDeviceId = bluetoothDevice
          ? bluetoothDevice.deviceId
          : audioInputDevices[0]?.deviceId;

        if (!preferredDeviceId) {
          throw new Error("No audio input devices available.");
        }
        // Get the stream for the preferred device
        return await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: preferredDeviceId } },
        });
      };

      if (permissionStatus.state === "granted") {
        const stream = await getPreferredMicStream();
        setMicStream(stream);
        setupScriptProcessor(stream);
        let mediaRecorder = new MediaRecorder(stream, {
          mimeType: AudioFormats.WEBM.mimeType,
        });
        
        // Monitor for microphone stream cancellation
        stream.getAudioTracks()[0].addEventListener("ended", () => {
          console.log("Microphone access was cancelled by user");
          setPermission("microphone", false, null);
          setPermission("micStream", false, null);
          setErrorMessage((e) => ({
            ...e,
            mic: "Microphone access was cancelled. Please grant access again.",
          }));
        });
        
        setErrorMessage((e) => ({ ...e, mic: "" }));
        setPermission("microphone", true, mediaRecorder);
        setPermission("micStream", true, stream);

        setMicTestPassed(true);
        return "granted";
      } else if (permissionStatus.state === "prompt") {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        setMicStream(stream);
        setupScriptProcessor(stream);
        let mediaRecorder = new MediaRecorder(stream, {
          mimeType: AudioFormats.WEBM.mimeType,
        });
        
        // Monitor for microphone stream cancellation
        stream.getAudioTracks()[0].addEventListener("ended", () => {
          console.log("Microphone access was cancelled by user");
          setPermission("microphone", false, null);
          setPermission("micStream", false, null);
          setErrorMessage((e) => ({
            ...e,
            mic: "Microphone access was cancelled. Please grant access again.",
          }));
        });
        
        setErrorMessage((e) => ({ ...e, mic: "" }));
        setPermission("microphone", true, mediaRecorder);
        setPermission("micStream", true, stream);

        setMicTestPassed(true);

        return "granted";
      } else {
        // Permission denied
        setErrorMessage((e) => ({
          ...e,
          mic: "Access denied. Please check site settings.",
        }));

        return "denied";
      }
    } catch (error) {
      console.error("Microphone permission denied.", error);
      setErrorMessage((e) => ({
        ...e,
        mic: "Microphone permission is required. Please allow access.",
      }));
      return "error";
    }
  };

  const handleCameraPermission = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "camera",
      });

      if (permissionStatus.state === "granted") {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        
        // Monitor for camera stream cancellation
        stream.getVideoTracks()[0].addEventListener("ended", () => {
          console.log("Camera access was cancelled by user");
          setPermission("webcam", false, null);
          setVideoStream(null);
          setErrorMessage((e) => ({
            ...e,
            camera: "Camera access was cancelled. Please grant access again.",
          }));
        });
        
        setErrorMessage((e) => ({ ...e, camera: "" }));
        setVideoStream(stream);
        setPermission("webcam", true, stream);

        return "granted";
      } else if (permissionStatus.state === "prompt") {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        
        // Monitor for camera stream cancellation
        stream.getVideoTracks()[0].addEventListener("ended", () => {
          console.log("Camera access was cancelled by user");
          setPermission("webcam", false, null);
          setVideoStream(null);
          setErrorMessage((e) => ({
            ...e,
            camera: "Camera access was cancelled. Please grant access again.",
          }));
        });
        
        setErrorMessage((e) => ({ ...e, camera: "" }));
        setVideoStream(stream);
        setPermission("webcam", true, stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        return "granted";
      } else {
        // Permission denied
        setErrorMessage((e) => ({
          ...e,
          camera: "Access denied. Please check site settings.",
        }));
        return "denied";
      }
    } catch (error) {
      console.error("Camera permission denied.", error);
      setErrorMessage((e) => ({
        ...e,
        camera: "Camera permission is required. Please allow access.",
      }));
      return "denied";
    }
  };

  const handleScreenSharingPermission = async () => {
    try {
      let constraints = {
        video: {
          width: { ideal: 854, max: 1280 },
          height: { ideal: 480, max: 720 },
          frameRate: { ideal: 12, max: 24 }
        },
        audio: {
          sampleRate: 22050,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      }
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: constraints.video,
        audio: constraints.audio,
        preferCurrentTab: true,
      });
      const userAudioForDisplayStream =
        await navigator.mediaDevices.getUserMedia({
          audio: constraints.audio,
        });

      // Monitor for screen sharing cancellation
      displayStream.getVideoTracks()[0].addEventListener("ended", () => {
        console.log("Screen sharing was cancelled by user");
        setPermission("tabDisplayRecording", false, null);
        setErrorMessage((e) => ({
          ...e,
          screen: "Screen sharing was cancelled. Please grant access again.",
        }));
      });

      setErrorMessage((e) => ({ ...e, screen: "" }));
      setPermission("tabDisplayRecording", true, displayStream);
      setPermission(
        "userAudioForDisplayStream",
        true,
        userAudioForDisplayStream
      );
      return "granted";
    } catch (error) {
      console.error("Screen sharing permission denied.", error);
      setErrorMessage((e) => ({
        ...e,
        screen: "Screen sharing permission is required. Please allow access.",
      }));
      return "error";
    }
  };

  // Permission request retry functions
  const requestMicUntilGranted = async () => {
    setErrorMessage((e) => ({
      ...e,
      mic: "",
    }));
    let status = "";
    while (status !== "granted") {
      status = await handleMicPermission();
      if (status === "denied") return "denied";
    }
    return "granted";
  };

  const requestCameraUntilGranted = async () => {
    setErrorMessage((e) => ({
      ...e,
      camera: "",
    }));
    let status = "";
    while (status !== "granted") {
      status = await handleCameraPermission();
      if (status === "denied") return "denied";
    }
    return "granted";
  };

  const requestScreenUntilGranted = async () => {
    setErrorMessage((e) => ({
      ...e,
      screen: "",
    }));
    let status =
      permissions?.tabDisplayRecording?.allowed &&
      permissions?.userAudioForDisplayStream?.allowed
        ? "granted"
        : "";

    while (status !== "granted") {
      status = await handleScreenSharingPermission();
      if (status === "denied") return "denied";
    }
    return "granted";
  };

  // ============================================================================
  // MAIN PERMISSION FLOW
  // ============================================================================

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startPermissionsFlow = useCallback(async (showErrors = true) => {
    setErrorMessage((e) => ({
      ...e,
      message: "",
    }));

    if (needMic) {
      const micStatus = await requestMicUntilGranted();
      if (micStatus === "denied") {
        if (showErrors) {
          setErrorMessage((e) => ({
            ...e,
            mic: "Permission Denied. Please check site settings and allow access.",
          }));
        }
        return; // Exit early if mic permission is denied
      }
    }

    if (needCam) {
      const camStatus = await requestCameraUntilGranted();
      if (camStatus === "denied") {
        if (showErrors) {
          setErrorMessage((e) => ({
            ...e,
            camera:
              "Permission Denied. Please check site settings and allow access.",
          }));
        }
        return; // Exit early if camera permission is denied
      }
    }

    if (needScreen) {
      const screenStatus = await requestScreenUntilGranted();
      if (screenStatus === "denied") {
        if (showErrors) {
          setErrorMessage((e) => ({
            ...e,
            screen:
              "Permission Denied. Please check site settings and allow access.",
          }));
        }
        return; // Exit early if screen permission is denied
      }
    }
  }, [needMic, needCam, needScreen]);

  // ============================================================================
  // INTERVIEW START HANDLER
  // ============================================================================

  const onStartInterview = async () => {
    const requiredPermissions = [];

    if (needCam) {
      requiredPermissions.push("webcam");
    }
    if (needScreen) {
      requiredPermissions.push(
        "tabDisplayRecording",
        "userAudioForDisplayStream"
      );
    }

    // Microphone is always required
    requiredPermissions.push("microphone");

    const allGranted = requiredPermissions.every(
      (perm) => permissions[perm]?.allowed
    );

    setPermission("allPermissionsGranted", "", "", allGranted);

    if (!allGranted) {
      setErrorMessage((e) => ({
        ...e,
        message: "Please provide all permissions to get started.",
      }));
    } else {
      setErrorMessage((e) => ({ ...e, message: "" }));
      setSetupTimedOut(false);
      setShouldStartTheInterview(true);
      setIsConnecting(true);
    }
  };

  // Called by MicCheck once the candidate either passes/accepts the result
  // (onPassed) or uses the escape hatch after repeated failures or a
  // Deepgram-side error (onSkipped, with a reason string for the console
  // trail below - not currently gated on, but useful if this needs
  // debugging from a candidate's session logs later).
  const handleMicCheckDone = (skipReason) => {
    if (skipReason) {
      console.log(`[MicCheck] Proceeding without a passing result: ${skipReason}`);
    }
    setPermission("isTestPassed", "", "", true);
    onStartInterview();
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Wait for isSetupDone once the interview has been kicked off, but don't wait
  // forever. The previous implementation was `while (!isSetupDone) { await
  // sleep(1000) }`, which captured isSetupDone from this render's closure and
  // so never observed the update - it span for the life of the page with no
  // failure path, which is how a failed LiveAvatar session token/start turned
  // into a permanently stuck "Setting up Connection..." spinner. This effect
  // re-runs whenever isSetupDone/sessionError change and gives up after
  // SETUP_TIMEOUT_MS instead.
  useEffect(() => {
    if (!isConnecting) return undefined;

    if (isSetupDone) {
      setIsConnecting(false);
      return undefined;
    }

    if (sessionError) {
      setIsConnecting(false);
      setSetupTimedOut(true);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setIsConnecting(false);
      setSetupTimedOut(true);
    }, SETUP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isConnecting, isSetupDone, sessionError]);

  // Video stream effect
  useEffect(() => {
    if (videoStream && videoRef.current) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  // Device type detection effect
  useEffect(() => {
    const mediaQueries = {
      phone: window.matchMedia("(max-width: 767px)"),
      tablet: window.matchMedia("(min-width: 768px) and (max-width: 1023px)"),
      desktop: window.matchMedia("(min-width: 1024px)"),
    };

    const updateDeviceType = () => {
      if (mediaQueries.phone.matches) setIsDesktop(false);
      else if (mediaQueries.tablet.matches) setIsDesktop(false);
      else setIsDesktop(true);
    };

    updateDeviceType();

    Object.values(mediaQueries).forEach((mq) =>
      mq.addEventListener("change", updateDeviceType)
    );

    return () => {
      Object.values(mediaQueries).forEach((mq) =>
        mq.removeEventListener("change", updateDeviceType)
      );
    };
  }, [setIsDesktop]);

  // Initial permissions flow effect
  useEffect(() => {
    startPermissionsFlow(false); // Don't show errors on initial load
  }, []);

  // Permissions state change effect
  useEffect(() => {
    if (
      permissions.microphone.allowed &&
      permissions.webcam.allowed &&
      permissions.tabDisplayRecording.allowed &&
      permissions.userAudioForDisplayStream.allowed
    ) {
      setErrorMessage((e) => ({
        ...e,
        message: "",
      }));
    }
  }, [permissions]);

  // ============================================================================
  // RENDER
  // ============================================================================


  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] flex justify-center items-center">
      <div className="flex flex-col items-center justify-center w-full">
        <div className="w-48 md:w-56 mb-5">
          <img src={logo} alt="Logo" />
        </div>

        <div className="w-full max-w-4xl bg-white rounded-xl shadow-md p-8">
          <h1 className="text-2xl font-semibold text-center bg-gradient-to-r from-[#4776e6] to-[#8E54E9] text-transparent bg-clip-text">
            Welcome to your interview session
            {firstName ? `, ${firstName}!` : ""}
          </h1>
          <p className="text-center text-xs text-gray-500 mt-2">
            Please complete the preparation steps below to ensure the best
            interview experience
          </p>

          {/* Stepper */}
          {needMicCheck ? (
            <div className="flex flex-wrap items-center justify-center mt-6 gap-x-3 gap-y-2">
              <div className="flex flex-col items-center text-app-primary">
                <div className="text-sm w-7 h-7 bg-app-primaryLight border border-app-primary rounded-full flex items-center justify-center font-semibold">
                  <Check size={15} className="text-app-primary" />
                </div>
                <p className="text-[13px] mt-1 font-medium text-app-primary">
                  Pre-Interview
                </p>
                <p className="text-[10px] text-gray-400">Complete checklist</p>
              </div>
              <div className="border-t border-app-primary h-[1px] w-10 sm:w-16"></div>
              {showMicTest ? (
                <div className="flex flex-col items-center text-app-primary">
                  <div className="text-sm w-7 h-7 bg-app-primaryLight border border-app-primary rounded-full flex items-center justify-center font-semibold">
                    <Check size={15} className="text-app-primary" />
                  </div>
                  <p className="text-[13px] mt-1 font-medium text-app-primary">
                    Device Setup
                  </p>
                  <p className="text-[10px] text-gray-400">Grant permissions</p>
                </div>
              ) : (
                <div className="flex flex-col items-center text-app-primary">
                  <div className="text-sm w-7 h-7 border border-app-primary text-app-primary rounded-full flex items-center justify-center font-semibold">
                    2
                  </div>
                  <p className="text-[13px] mt-1 font-medium">Device Setup</p>
                  <p className="text-[10px] text-gray-400">Grant permissions</p>
                </div>
              )}
              <div
                className={`border-t h-[1px] w-10 sm:w-16 ${
                  showMicTest ? "border-app-primary" : "border-gray-300"
                }`}
              ></div>
              {showMicTest ? (
                <div className="flex flex-col items-center text-app-primary">
                  <div className="text-sm w-7 h-7 border border-app-primary text-app-primary rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <p className="text-[13px] mt-1 font-medium">Mic Check</p>
                  <p className="text-[10px] text-gray-400">Test your microphone</p>
                </div>
              ) : (
                <div className="flex flex-col items-center text-gray-400">
                  <div className="text-sm w-7 h-7 border border-gray-300 rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <p className="text-[13px] mt-1 font-medium">Mic Check</p>
                  <p className="text-[10px] text-gray-400">Test your microphone</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center mt-6 space-x-5">
              <div className="flex flex-col items-center text-app-primary">
                <div className="text-sm w-7 h-7 bg-app-primaryLight border border-app-primary rounded-full flex items-center justify-center font-semibold">
                  <Check size={15} className="text-app-primary" />
                </div>
                <p className="text-[13px] mt-1 font-medium text-app-primary">
                  Pre-Interview
                </p>
                <p className="text-[10px] text-gray-400">Complete checklist</p>
              </div>
              <div className="border-t border-app-primary h-[1px] w-20"></div>
              <div className="flex flex-col items-center  text-app-primary">
                <div className="text-sm w-7 h-7 border  border-app-primary text-app-primary rounded-full flex items-center justify-center font-semibold">
                  2
                </div>
                <p className="text-[13px] mt-1 font-medium">Device Setup</p>
                <p className="text-[10px] text-gray-400">Grant permissions</p>
              </div>
            </div>
          )}

          {/* Interview Details - hidden once the candidate has moved on to
              the Mic Check step, so that step gets the card's full focus. */}
          {!showMicTest && (
            <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-lg p-4 mt-6">
              <div className="mb-5 flex items-center gap-x-2">
                <Calendar size={16} className="text-app-primary" />
                <p className="text-[#101828] text-sm font-medium">
                  Interview Details
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px] font-medium text-gray-600">
                <>
                  <div>
                    <p className="text-gray-400 font-normal">Role</p>
                    <p className="text-[#101828] text-xs font-medium">
                      {jobTitle}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-normal">Date</p>
                    <p className="text-[#101828] text-xs font-medium">
                      {new Date().toDateString()}
                    </p>
                  </div>{" "}
                  <div>
                    <p className="text-gray-400 font-normal">Interview Type</p>
                    <div className="flex items-center gap-1 text-[#101828] font-semibold gap-x-2">
                      {mode === "web_video" ? (
                        <Video size={14} className="text-app-primary" />
                      ) : mode === "web_video_without_avatar" ? (
                        <Video size={14} className="text-app-primary" />
                      ) : mode === "web_audio" ? (
                        <Headphones size={14} className="text-app-primary" />
                      ) : (
                        <PhoneCall size={14} className="text-app-primary" />
                      )}
                      <p className="text-[#101828] text-xs font-medium">
                        {mode === "web_video"
                          ? "Video Interview"
                          : mode === "web_video_without_avatar"
                          ? "Video Interview"
                          : mode === "web_audio"
                          ? "Audio Interview"
                          : "Telephone Interview"}
                      </p>
                    </div>
                  </div>
                </>
              </div>
            </div>
          )}

          <div className="flex flex-col mt-5 gap-x-10 items-center justify-center">
            {!showMicTest ? (
              <>
            <div className="mt-6 w-full">
              <div className="flex items-center justify-start gap-x-2">
                <Lock size={16} className="text-app-primary" />
                <h5 className="text-app-primary text-sm font-semibold ">
                  Device Permissions Required
                </h5>
              </div>
              <p className="w-full mt-2 mb-4 p-0 text-xs">
                For a successful video interview experience, you must allow
                access to the following:
              </p>
              <div className="flex flex-col sm:flex-row justify-start gap-y-3 gap-x-3">
                <div className="border rounded-lg p-4 w-full sm:w-[33%] shadow-sm">
                  <div className="flex flex-col items-start space-x-2">
                    <div className="w-full flex justify-between items-center mb-3">
                      <div className="flex items-center gap-x-2">
                        <Mic className="text-app-primary" size={16} />
                        <p className=" text-sm text-gray-800 font-medium">
                          Microphone
                        </p>
                      </div>
                      {permissions.microphone.allowed && (
                        <Check size={17} className="text-app-success" />
                      )}
                    </div>

                    <p className="text-xs text-gray-600 p-0 ">
                      Required for clear audio <br />
                      communication during the interview
                    </p>
                  </div>

                  <div
                    className={`${
                      permissions.microphone.allowed
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-900  cursor-pointer"
                    }  mt-3 max-w-max min-w-max px-3 font-medium py-1 border rounded-md text-[13px] hover:bg-gray-50 transition`}
                    onClick={
                      permissions.microphone.allowed
                        ? () => {}
                        : () => startPermissionsFlow()
                    }
                  >
                    {permissions.microphone.allowed
                      ? "Granted"
                      : "Grant Access"}
                  </div>
                  {errorMessage.mic !== "" && (
                    <p className="w-full text-start text-[10px] font-medium text-app-danger mt-2">
                      {errorMessage.mic}
                    </p>
                  )}
                </div>
                {needCam && (
                  <div className="border rounded-lg p-4 w-full sm:w-[33%]  shadow-sm">
                    <div className="flex flex-col items-start space-x-2">
                      <div className="w-full flex justify-between items-center mb-3">
                        <div className="flex items-center gap-x-2">
                          <Camera className="text-app-primary" size={16} />
                          <p className=" text-sm text-gray-800 font-medium">
                            Camera
                          </p>
                        </div>
                        {permissions.webcam.allowed && (
                          <Check size={17} className="text-app-success" />
                        )}
                      </div>

                      <p className="text-xs text-gray-600 p-0 ">
                        Required for face-to-face interaction with the
                        interviewer
                      </p>
                    </div>

                    <div
                      className={`${
                        permissions.webcam.allowed
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-900  cursor-pointer"
                      }  mt-3 max-w-max min-w-max px-3 font-medium py-1 border rounded-md text-[13px] hover:bg-gray-50 transition`}
                      onClick={
                        permissions.webcam.allowed
                          ? () => {}
                          : () => startPermissionsFlow()
                      }
                    >
                      {permissions.webcam.allowed
                        ? "Granted"
                        : "Grant Access"}
                    </div>
                    {errorMessage.camera !== "" && (
                      <p className="w-full text-start text-[10px] font-medium text-app-danger mt-2">
                        {errorMessage.camera}
                      </p>
                    )}
                  </div>
                )}
                {needScreen && (
                  <div className="border rounded-lg p-4 w-full sm:w-[33%]  shadow-sm">
                    <div className="flex flex-col items-start space-x-2">
                      <div className="w-full flex justify-between items-center mb-3">
                        <div className="flex items-center gap-x-2">
                          <Monitor className="text-app-primary" size={16} />
                          <p className=" text-sm text-gray-800 font-medium">
                            Screen share
                          </p>
                        </div>
                        {permissions.tabDisplayRecording.allowed &&
                          permissions.userAudioForDisplayStream.allowed && (
                            <Check size={17} className="text-app-success" />
                          )}
                      </div>

                      <p className="text-xs text-gray-600 p-0 ">
                        Could be required for technical assessments or
                        demonstrations.
                      </p>
                    </div>

                    <div
                      className={`${
                        permissions.tabDisplayRecording.allowed &&
                        permissions.userAudioForDisplayStream.allowed
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-900  cursor-pointer"
                      }  mt-3 max-w-max min-w-max px-3 py-1 border font-semibold rounded-md text-[13px]  hover:bg-gray-50 transition`}
                      onClick={
                        permissions.tabDisplayRecording.allowed &&
                        permissions.userAudioForDisplayStream.allowed
                          ? () => {}
                          : () => startPermissionsFlow()
                      }
                    >
                      {permissions.tabDisplayRecording.allowed &&
                      permissions.userAudioForDisplayStream.allowed
                        ? "Granted"
                        : "Grant Access"}
                    </div>
                    {errorMessage.screen !== "" && (
                      <p className="w-full text-start text-[10px] font-medium text-app-danger mt-2">
                        {errorMessage.screen}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            {errorMessage.message !== "" && (
              <p className="w-full text-start text-xs text-app-danger mt-1">
                {errorMessage.message}
              </p>
            )}
            <div className="flex w-full items-center justify-end mt-5">
              {/* <p className="">Ready to Join?</p> */}
              <div
                data-testid="permission-primary-action"
                className={`
                  px-5 py-2 rounded-md flex justify-center items-center text-sm font-medium w-40 ${
                    isConnecting || !areAllRequiredPermissionsGranted()
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-app-primary text-white cursor-pointer hover:bg-app-primary/90"
                  }`}
                onClick={
                  isConnecting || !areAllRequiredPermissionsGranted()
                    ? () => {}
                    : needMicCheck
                    ? () => setShowMicTest(true)
                    : onStartInterview
                }
              >
                {isConnecting ? (
                  <span className="loading loading-dots loading-sm text-white"></span>
                ) : needMicCheck ? (
                  "Continue"
                ) : (
                  "Start Interview"
                )}
              </div>
            </div>
              </>
            ) : (
              // Once onStartInterview has actually been called, Mic Check's
              // job is done - if the LiveAvatar/Ultravox connection itself
              // then fails, that surfaces via setupTimedOut's own "Reload
              // page" recovery below, not by resurrecting a passed/skipped
              // Mic Check just because isConnecting flipped back to false.
              !isConnecting &&
              !setupTimedOut && (
                <MicCheck
                  micStream={permissions.micStream?.stream}
                  onPassed={handleMicCheckDone}
                  onSkipped={handleMicCheckDone}
                />
              )
            )}
            {isConnecting && (
              <div>
                <p className="text-sm text-app-primary font-semibold ">
                  Setting up Connection...
                </p>
              </div>
            )}
            {setupTimedOut && (
              <div>
                <p className="w-full text-start text-xs text-app-danger mt-1">
                  {sessionError ||
                    "We couldn't connect to the interview session. Please check your connection and try again."}
                </p>
                {/* A full reload is the reliable retry here: the LiveAvatar
                    session is set up once per page load, so re-clicking Start
                    Interview without reloading would just wait on the same
                    failed session again. */}
                <p
                  className="text-xs font-semibold text-app-primary underline cursor-pointer mt-1"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionsScreenNew;
